/**
 * Fuzzy search utilities. SEM lib externa — implementação leve focada em:
 *   - case-insensitive
 *   - acentos ignorados (`ç` ≈ `c`, `á` ≈ `a`)
 *   - subsequence match: "evelope" encontra "ENVELOPE" (1 char faltando)
 *   - substring exact: continua sendo o mais rápido
 *
 * Não usa Levenshtein full pra evitar O(m*n) por row. Subsequence cobre o
 * caso "faltando 1+ char" que o usuário reportou. Pra typo no MEIO ("ebvelope"
 * em vez de "envelope"), token split tenta cada palavra do field.
 */

/** Lowercase + remove acentos. Compatível com Unicode normalizado. */
function normalize(value) {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Subsequência COM CAP DE SPAN — Q é subseq de S, MAS exige que o span (índice
 * do último char casado − índice do primeiro) caiba em `query.length * SPAN_MAX_RATIO`.
 *
 * Sem cap, "envelope" (8 chars) casava com "MINDSET ... PORTUGUES 2017" porque
 * todos os chars E,N,V,E,L,O,P,E aparecem scattered no texto. Com cap span ≤ 16
 * (8 × 2), a subseq tem que estar compactada — só passa se for typo/missing
 * char em palavra real.
 */
const SPAN_MAX_RATIO = 2;
function isCompactSubsequence(query, source) {
    let i = 0;
    let firstHit = -1;
    let lastHit = -1;
    for (let j = 0; j < source.length && i < query.length; j += 1) {
        if (source[j] === query[i]) {
            if (firstHit === -1) firstHit = j;
            lastHit = j;
            i += 1;
        }
    }
    if (i !== query.length) return false;
    const span = lastHit - firstHit + 1;
    return span <= query.length * SPAN_MAX_RATIO;
}

/**
 * True se `query` casa com ALGUM dos `fields` por:
 *   1. Substring (rápido) — case/acento insensitive.
 *   2. Subsequence — cobre "1+ char faltando".
 *   3. Token-substring — split por espaço, tenta substring em cada palavra.
 *
 * Query vazia/whitespace → true (não filtra).
 */
export function FuzzyMatch(query, fields) {
    const q = normalize(query).trim();
    if (!q) return true;

    for (const raw of fields) {
        if (raw === null || raw === undefined) continue;
        if (Array.isArray(raw)) {
            // Lista (tags, mlbs) — match em qualquer item.
            for (const item of raw) {
                if (matchesOneField(q, normalize(item))) return true;
            }
            continue;
        }
        if (matchesOneField(q, normalize(raw))) return true;
    }
    return false;
}

function matchesOneField(q, s) {
    if (!s) return false;
    if (s.includes(q)) return true; // 1. substring (fastest, e mais comum)
    // 2. token-substring: split por espaço/separadores e tenta substring +
    //    subseq compacta em cada palavra. Mais restrito que rodar subseq no
    //    field inteiro — evita "envelope" achar tudo num field longo.
    const tokens = s.split(/[\s,;|/-]+/);
    if (tokens.length > 1) {
        for (const token of tokens) {
            if (!token) continue;
            if (token.includes(q)) return true;
            if (isCompactSubsequence(q, token)) return true;
        }
        return false;
    }
    // 3. field é uma palavra só → subseq compacta no field inteiro (mas com
    //    span cap, então só passa se chars de q estão próximos).
    return isCompactSubsequence(q, s);
}
